import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export default function AdminFinancials() {
  const { canEdit, canAdd } = usePermissions();
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Member search
  const [memberSearch, setMemberSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const dropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    member_id: '', transaction_type: 'CRJ', payment_type: 'annual_dues', amount: '',
    debit_amount: '', credit_amount: '', passthrough: false,
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  // Pending member payments
  const [pendingPayments, setPendingPayments] = useState([]);
  const [approvingId, setApprovingId] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editData, setEditData] = useState({ payment_type: '', amount: '', description: '', passthrough: false });

  // Failed payments
  const [failedPayments, setFailedPayments] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [viewingPayment, setViewingPayment] = useState(null);

  // Search & date range filter
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Auto-toggle pass-through for certain payment types
  const PASSTHROUGH_TYPES = ['agm', 'hotel', 'mcpd', 'form'];

  // Bulk debit/credit
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkType, setBulkType] = useState('SJ'); // SJ = bulk debit, CRJ = bulk credit
  const [bulkData, setBulkData] = useState({
    amount: '', description: '', date: new Date().toISOString().split('T')[0],
    payment_type: 'annual_dues', passthrough: false,
  });
  const [bulkLoading, setBulkLoading] = useState(false);

  // Record transaction
  const [recording, setRecording] = useState(false);

  // Reverse bulk
  const [showReverseModal, setShowReverseModal] = useState(false);
  const [bulkBatches, setBulkBatches] = useState([]);
  const [reversingBatchId, setReversingBatchId] = useState(null);
  const [batchesLoading, setBatchesLoading] = useState(false);

  const openBulkModal = (type) => {
    setBulkType(type);
    setBulkData({ amount: '', description: '', date: new Date().toISOString().split('T')[0], payment_type: 'annual_dues', passthrough: false });
    setShowBulkModal(true);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setBulkLoading(true);
    try {
      const res = await api.post('/admin/ledger/bulk/', {
        entry_type: bulkType,
        amount: bulkData.amount,
        description: bulkData.description,
        date: bulkData.date,
        passthrough: bulkData.passthrough,
      });
      toast.success(res.data.detail || `${res.data.count} entries created!`);
      setShowBulkModal(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk operation failed.');
    } finally {
      setBulkLoading(false);
    }
  };

  const openReverseModal = async () => {
    setShowReverseModal(true);
    setBatchesLoading(true);
    try {
      const res = await api.get('/admin/ledger/bulk/batches/');
      setBulkBatches(res.data);
    } catch {
      toast.error('Failed to load bulk batches.');
    } finally {
      setBatchesLoading(false);
    }
  };

  const handleReverseBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to reverse this entire bulk operation? This will permanently delete all entries in this batch.')) return;
    setReversingBatchId(batchId);
    try {
      const res = await api.post(`/admin/ledger/bulk/${batchId}/reverse/`);
      toast.success(res.data.detail || 'Batch reversed successfully.');
      setBulkBatches(prev => prev.filter(b => b.batch_id !== batchId));
      fetchData();
    } catch {
      toast.error('Failed to reverse batch.');
    } finally {
      setReversingBatchId(null);
    }
  };

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/ledger/'),
      api.get('/accounts/admin/members/?page_size=1000'),
      api.get('/admin/payments/?status=pending'),
      api.get('/admin/payments/failed/'),
    ]).then(([ledgerRes, membersRes, pendingRes, failedRes]) => {
      setLedgerEntries(ledgerRes.data.results || ledgerRes.data);
      setMembers(membersRes.data.results || membersRes.data);
      setPendingPayments(pendingRes.data.results || pendingRes.data);
      setFailedPayments(failedRes.data.results || failedRes.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filteredMembers = members.filter(m => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return true;
    const surname = (m.surname || m.last_name || m.user?.last_name || '').toLowerCase();
    const otherNames = (m.other_names || m.first_name || m.user?.first_name || '').toLowerCase();
    const fullName = (m.full_name || '').toLowerCase();
    const num = (m.membership_number || '').toLowerCase();
    const email = (m.email || m.user?.email || '').toLowerCase();
    const haystack = `${surname} ${otherNames} ${otherNames} ${surname} ${fullName} ${num} ${email}`;
    const words = q.split(/\s+/);
    return haystack.includes(q) || words.every(w => haystack.includes(w));
  });

  const selectMember = (m) => {
    setSelectedMember(m);
    setMemberSearch(`${m.full_name || ''} — ${m.membership_number || 'N/A'}`);
    setFormData({ ...formData, member_id: m.id });
    setShowDropdown(false);
  };

  const clearMember = () => {
    setSelectedMember(null);
    setMemberSearch('');
    setFormData({ ...formData, member_id: '' });
  };

  const handleRecord = async (e) => {
    e.preventDefault();
    if (recording) return; // guard against double-submit creating duplicate payments
    if (!selectedMember) { toast.error('Please select a member.'); return; }
    setRecording(true);
    try {
      await api.post('/admin/payments/record/', formData);
      fetchData();
      setShowForm(false);
      clearMember();
      setFormData({ member_id: '', transaction_type: 'CRJ', payment_type: 'annual_dues', amount: '', debit_amount: '', credit_amount: '', passthrough: false, description: '', date: new Date().toISOString().split('T')[0] });
      toast.success('Transaction posted!');
    } catch {
      toast.error('Failed to record transaction.');
    } finally {
      setRecording(false);
    }
  };

  const openEditModal = (p) => {
    setEditingPayment(p);
    setEditData({
      payment_type: p.payment_type,
      amount: p.amount,
      description: p.description || '',
      passthrough: PASSTHROUGH_TYPES.includes(p.payment_type),
    });
  };

  const handleApprove = async (paymentId, overrides = {}) => {
    setApprovingId(paymentId);
    try {
      await api.post(`/admin/payments/${paymentId}/approve/`, overrides);
      toast.success('Payment approved and ledger entry created.');
      setEditingPayment(null);
      fetchData();
    } catch {
      toast.error('Failed to approve payment.');
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (paymentId) => {
    if (!window.confirm('Are you sure you want to decline this payment?')) return;
    setApprovingId(paymentId);
    try {
      await api.post(`/admin/payments/${paymentId}/reject/`);
      toast.success('Payment declined.');
      fetchData();
    } catch {
      toast.error('Failed to reject payment.');
    } finally {
      setApprovingId(null);
    }
  };

  const handleDeleteFailed = async (paymentId) => {
    if (!window.confirm('Are you sure you want to permanently delete this failed payment record?')) return;
    setDeletingId(paymentId);
    try {
      await api.delete(`/admin/payments/${paymentId}/delete/`);
      toast.success('Failed payment deleted.');
      fetchData();
    } catch {
      toast.error('Failed to delete payment.');
    } finally {
      setDeletingId(null);
    }
  };

  // Filter entries by search query and date range
  const filteredEntries = ledgerEntries.filter(e => {
    // Text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = (
        (e.member_name || '').toLowerCase().includes(q) ||
        (e.membership_number || '').toLowerCase().includes(q) ||
        (e.transaction_number || '').toLowerCase().includes(q) ||
        (e.description || '').toLowerCase().includes(q) ||
        (e.entry_type || '').toLowerCase().includes(q)
      );
      if (!match) return false;
    }
    // Date range
    if (dateFrom && e.date < dateFrom) return false;
    if (dateTo && e.date > dateTo) return false;
    return true;
  });

  const totalDebit = filteredEntries.reduce((s, e) => s + Number(e.debit_amount || 0), 0);
  const totalCredit = filteredEntries.reduce((s, e) => s + Number(e.credit_amount || 0), 0);

  // Pagination calculations
  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);
  const paginatedEntries = filteredEntries.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, dateFrom, dateTo, ledgerEntries]);

  // === Download PDF ===
  const downloadPDF = async () => {
    try {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const usableWidth = pageWidth - margin * 2;

    // Logo
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => { logoImg.onload = resolve; logoImg.onerror = reject; logoImg.src = '/NIS ABJ LOGO.jpg'; });
      doc.addImage(logoImg, 'JPEG', margin, 8, 22, 22);
    } catch {}

    // Title
    doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('NIGERIAN INSTITUTION OF SURVEYORS', 40, 15);
    doc.setFontSize(12);
    doc.text('FCT BRANCH — Financial Transactions', 40, 22);

    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    let nextY = 38;
    if (dateFrom || dateTo) {
      let dateRangeText = '';
      if (dateFrom && dateTo) dateRangeText = `Period: ${dateFrom} to ${dateTo}`;
      else if (dateFrom) dateRangeText = `Period: From ${dateFrom}`;
      else dateRangeText = `Period: Up to ${dateTo}`;
      doc.text(dateRangeText, margin, nextY);
      nextY += 6;
    }
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, nextY);
    const tableStartY = nextY + 6;

    // Column widths (8 columns, landscape A4 ~269mm usable)
    const colCustId = 28;
    const colCustName = 45;
    const colDate = 26;
    const colTrans = 26;
    const colType = 18;
    const colDebit = 32;
    const colCredit = 32;
    const colDesc = usableWidth - colCustId - colCustName - colDate - colTrans - colType - colDebit - colCredit;

    const balance = totalDebit - totalCredit;
    const balanceStr = balance > 0 ? `-N${balance.toLocaleString()}` : `N${Math.abs(balance).toLocaleString()}`;

    // Body rows
    const bodyRows = filteredEntries.map(e => [
      e.membership_number || '—',
      e.member_name,
      e.date,
      e.transaction_number,
      e.entry_type,
      Number(e.debit_amount) > 0 ? `N${Number(e.debit_amount).toLocaleString()}` : '',
      Number(e.credit_amount) > 0 ? `N${Number(e.credit_amount).toLocaleString()}` : '',
      e.description,
    ]);

    // Total row
    bodyRows.push([
      { content: '', styles: {} },
      { content: '', styles: {} },
      { content: '', styles: {} },
      { content: '', styles: {} },
      { content: 'Report Total', styles: { fontStyle: 'bold', halign: 'right' } },
      { content: `N${totalDebit.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: `N${totalCredit.toLocaleString()}`, styles: { fontStyle: 'bold', halign: 'right' } },
      { content: `Balance: ${balanceStr}`, styles: { fontStyle: 'bold', halign: 'right' } },
    ]);

    autoTable(doc, {
      startY: tableStartY,
      margin: { left: margin, right: margin },
      tableWidth: usableWidth,
      head: [['Member ID', 'Member', 'Date', 'Trans No', 'Type', 'Debit Amt', 'Credit Amt', 'Description']],
      body: bodyRows,
      styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', lineColor: [200, 200, 200], lineWidth: 0.2 },
      headStyles: { fillColor: [11, 93, 30], textColor: 255, fontStyle: 'bold', halign: 'center', cellPadding: 4 },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      columnStyles: {
        0: { cellWidth: colCustId, halign: 'center' },
        1: { cellWidth: colCustName, halign: 'left' },
        2: { cellWidth: colDate, halign: 'center' },
        3: { cellWidth: colTrans, halign: 'center' },
        4: { cellWidth: colType, halign: 'center' },
        5: { cellWidth: colDebit, halign: 'right' },
        6: { cellWidth: colCredit, halign: 'right' },
        7: { cellWidth: colDesc, halign: 'left' },
      },
      didParseCell: (data) => {
        const isLastRow = data.row.index === bodyRows.length - 1;
        // Debit column (5) — red
        if (data.column.index === 5 && data.cell.raw !== '' && data.section === 'body') {
          data.cell.styles.textColor = [220, 38, 38];
        }
        // Credit column (6) — green
        if (data.column.index === 6 && data.cell.raw !== '' && data.section === 'body') {
          data.cell.styles.textColor = [22, 163, 74];
        }
        // Balance in Description column of total row — red if debit, green if credit
        if (data.column.index === 7 && data.section === 'body' && isLastRow) {
          data.cell.styles.textColor = balance > 0 ? [220, 38, 38] : [22, 163, 74];
        }
        // Total row styling
        if (isLastRow) {
          data.cell.styles.fillColor = [235, 235, 235];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });

    doc.save('Financials_Report.pdf');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF. Check console for details.');
    }
  };

  // === Export to Excel ===
  const exportExcel = () => {
    try {
    // Header rows
    const headerRows = [
      { 'Member ID': 'NIGERIAN INSTITUTION OF SURVEYORS — FCT BRANCH', 'Member': '', 'Date': '', 'Trans No': '', 'Type': '', 'Debit Amt': '', 'Credit Amt': '', 'Description': '' },
    ];
    if (dateFrom || dateTo) {
      let dateRangeText = '';
      if (dateFrom && dateTo) dateRangeText = `Period: ${dateFrom} to ${dateTo}`;
      else if (dateFrom) dateRangeText = `Period: From ${dateFrom}`;
      else dateRangeText = `Period: Up to ${dateTo}`;
      headerRows.push({ 'Member ID': dateRangeText, 'Member': '', 'Date': '', 'Trans No': '', 'Type': '', 'Debit Amt': '', 'Credit Amt': '', 'Description': '' });
    }
    headerRows.push({ 'Member ID': `Generated: ${new Date().toLocaleDateString()}`, 'Member': '', 'Date': '', 'Trans No': '', 'Type': '', 'Debit Amt': '', 'Credit Amt': '', 'Description': '' });
    headerRows.push({ 'Member ID': '', 'Member': '', 'Date': '', 'Trans No': '', 'Type': '', 'Debit Amt': '', 'Credit Amt': '', 'Description': '' });

    const balance = totalDebit - totalCredit;
    const balanceStr = balance > 0 ? `-₦${balance.toLocaleString()}` : `₦${Math.abs(balance).toLocaleString()}`;

    const rows = filteredEntries.map(e => ({
      'Member ID': e.membership_number || '—',
      'Member': e.member_name,
      'Date': e.date,
      'Trans No': e.transaction_number,
      'Type': e.entry_type,
      'Debit Amt': Number(e.debit_amount) || 0,
      'Credit Amt': Number(e.credit_amount) || 0,
      'Description': e.description,
    }));
    rows.push({ 'Member ID': '', 'Member': '', 'Date': '', 'Trans No': '', 'Type': 'REPORT TOTAL', 'Debit Amt': totalDebit, 'Credit Amt': totalCredit, 'Description': `Balance: ${balanceStr}` });
    const ws = XLSX.utils.json_to_sheet([...headerRows, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Financials');
    XLSX.writeFile(wb, 'Financials_Report.xlsx');
    } catch (err) {
      console.error('Excel export error:', err);
      toast.error('Failed to export Excel. Check console for details.');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Financials</h1>
          <div className="flex gap-2 flex-wrap shrink-0">
            {canAdd(PERMISSIONS.ADD_FINANCIALS) && (
              <button onClick={() => openBulkModal('SJ')}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
                Bulk Debit
              </button>
            )}
            {canAdd(PERMISSIONS.ADD_FINANCIALS) && (
              <button onClick={() => openBulkModal('CRJ')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>
                Bulk Credit
              </button>
            )}
            {canAdd(PERMISSIONS.ADD_FINANCIALS) && (
              <button onClick={openReverseModal}
                className="bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 transition flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                Reverse Bulk
              </button>
            )}
            {canAdd(PERMISSIONS.ADD_FINANCIALS) && (
              <button onClick={() => { setShowForm(!showForm); if (showForm) { clearMember(); } }}
                className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
                {showForm ? 'Cancel' : 'Post Transaction'}
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Total Debit: <strong className="text-red-600">₦{totalDebit.toLocaleString()}</strong>
          {' | '}
          Total Credit: <strong className="text-green-600">₦{totalCredit.toLocaleString()}</strong>
          {' | '}
          Balance: <strong className={totalDebit - totalCredit > 0 ? 'text-red-600' : 'text-green-600'}>₦{Math.abs(totalDebit - totalCredit).toLocaleString()}</strong>
        </p>
      </div>

      {showForm && (
        <form onSubmit={handleRecord} className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Member Search Selector */}
            <div className="md:col-span-2" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-500 mb-1">Select Member</label>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => { setMemberSearch(e.target.value); setShowDropdown(true); if (selectedMember) { setSelectedMember(null); setFormData({ ...formData, member_id: '' }); } }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Type member name or membership number..."
                  className="w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                />
                {selectedMember && (
                  <button type="button" onClick={clearMember}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
                {showDropdown && memberSearch && !selectedMember && (
                  <div className="absolute z-20 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredMembers.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-400">No members found</div>
                    ) : filteredMembers.slice(0, 10).map(m => (
                      <button type="button" key={m.id} onClick={() => selectMember(m)}
                        className="w-full text-left px-4 py-2.5 hover:bg-green-50 flex justify-between items-center border-b last:border-b-0">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{m.full_name}</div>
                          <div className="text-xs text-gray-500">{m.membership_number || 'No ID'}</div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          m.membership_status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>{m.membership_status}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedMember && (
                <div className="mt-2 flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  <span><strong>{selectedMember.full_name}</strong> — {selectedMember.membership_number || 'N/A'} — {selectedMember.membership_category || 'N/A'}</span>
                </div>
              )}
            </div>

            {/* Type of Transaction */}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Type of Transaction</label>
              <select value={formData.transaction_type} onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                <option value="SJ">SJ — Sales Journal (Debit/Charge)</option>
                <option value="CRJ">CRJ — Cash Receipt Journal (Credit/Payment)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Payment Type</label>
              <select value={formData.payment_type} onChange={(e) => {
                const pt = e.target.value;
                const autoPass = PASSTHROUGH_TYPES.includes(pt);
                setFormData({ ...formData, payment_type: pt, passthrough: autoPass, transaction_type: autoPass ? 'CRJ' : formData.transaction_type });
              }}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                <option value="annual_dues">Annual Dues</option>
                <option value="registration">Registration</option>
                <option value="event">Event Fee</option>
                <option value="agm">AGM</option>
                <option value="mcpd">MCPD</option>
                <option value="form">Form</option>
                <option value="hotel">Hotel</option>
                <option value="penalty">Penalty</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Pass-through toggle for CRJ */}
            {formData.transaction_type === 'CRJ' && (
              <div className="md:col-span-2 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                <input type="checkbox" id="passthrough" checked={formData.passthrough}
                  onChange={(e) => setFormData({ ...formData, passthrough: e.target.checked })}
                  className="w-4 h-4 accent-[#0B5D1E] rounded" />
                <label htmlFor="passthrough" className="text-sm text-amber-800">
                  <strong>Pass-through</strong> — Debit &amp; Credit are equal, <em>no effect on balance</em> (e.g. AGM, Hotel, MCPD, Form)
                </label>
              </div>
            )}

            {/* Amount fields based on transaction type */}
            {formData.transaction_type === 'SJ' ? (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Debit Amount (₦)</label>
                <input type="number" step="0.01" required value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                  placeholder="0.00" />
              </div>
            ) : formData.passthrough ? (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Amount (₦) — <span className="text-amber-600">Debit &amp; Credit will be equal</span></label>
                <input type="number" step="0.01" required value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value, debit_amount: e.target.value, credit_amount: e.target.value })}
                  className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none text-sm bg-amber-50"
                  placeholder="0.00" />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Debit Amount (₦)</label>
                  <input type="number" step="0.01" value={formData.debit_amount}
                    onChange={(e) => setFormData({ ...formData, debit_amount: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                    placeholder="0.00 (optional)" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Credit Amount (₦)</label>
                  <input type="number" step="0.01" required value={formData.credit_amount || formData.amount}
                    onChange={(e) => setFormData({ ...formData, credit_amount: e.target.value, amount: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                    placeholder="0.00" />
                </div>
              </>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Date</label>
              <input type="date" required value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
              <input type="text" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                placeholder="Optional note" />
            </div>
          </div>
          <button type="submit" disabled={recording} className="mt-4 bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed">{recording ? 'Posting...' : 'Post Transaction'}</button>
        </form>
      )}

      {/* Pending Member-Initiated Payments */}
      {pendingPayments.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="px-4 py-3 border-b bg-yellow-50 rounded-t-xl flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-sm font-bold text-yellow-800">Pending Member Payments ({pendingPayments.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Method</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Year</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Reference</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Proof</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pendingPayments.map((p) => (
                  <tr key={p.id} className="hover:bg-yellow-50/50 cursor-pointer" onClick={() => setViewingPayment(p)}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.member_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm capitalize">{p.payment_type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">₦{Number(p.amount).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-semibold ${
                        p.payment_method === 'paystack' ? 'bg-blue-100 text-blue-700' :
                        p.payment_method === 'manual' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{p.payment_method === 'paystack' ? 'Paystack' : p.payment_method === 'manual' ? 'Bank Transfer' : p.payment_method || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{p.year}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{p.reference}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.description || '—'}</td>
                    <td className="px-4 py-3 text-sm" onClick={e => e.stopPropagation()}>
                      {p.proof_of_payment ? (
                        <a href={p.proof_of_payment} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B5D1E] hover:underline">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                          View
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-700">Pending</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => openEditModal(p)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-blue-700 transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleApprove(p.id)}
                          disabled={approvingId === p.id}
                          className="bg-green-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-green-700 transition disabled:opacity-50"
                        >
                          {approvingId === p.id ? '...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleReject(p.id)}
                          disabled={approvingId === p.id}
                          className="bg-red-500 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-red-600 transition disabled:opacity-50"
                        >
                          Decline
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Failed Transactions */}
      {failedPayments.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border mb-6">
          <div className="px-4 py-3 border-b bg-red-50 rounded-t-xl flex items-center gap-2">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h2 className="text-sm font-bold text-red-800">Failed Transactions ({failedPayments.length})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member ID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Method</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Year</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Reference</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {failedPayments.map((p, idx) => (
                  <tr key={p.id} className="hover:bg-red-50/50 cursor-pointer" onClick={() => setViewingPayment(p)}>
                    <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.member_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{p.member_membership_number || '—'}</td>
                    <td className="px-4 py-3 text-sm capitalize">{p.payment_type?.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">₦{Number(p.amount).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-semibold ${
                        p.payment_method === 'paystack' ? 'bg-blue-100 text-blue-700' :
                        p.payment_method === 'manual' ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{p.payment_method === 'paystack' ? 'Paystack' : p.payment_method === 'manual' ? 'Bank Transfer' : p.payment_method || '—'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">{p.year}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{p.reference}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.description || '—'}</td>
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleDeleteFailed(p.id)}
                        disabled={deletingId === p.id}
                        className="bg-red-500 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-red-600 transition disabled:opacity-50"
                      >
                        {deletingId === p.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Payment Modal */}
      {editingPayment && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setEditingPayment(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Edit & Approve Payment</h3>
                <p className="text-xs text-gray-500">{editingPayment.member_name} — {editingPayment.reference}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Payment Type</label>
                <select value={editData.payment_type}
                  onChange={(e) => {
                    const pt = e.target.value;
                    setEditData({ ...editData, payment_type: pt, passthrough: PASSTHROUGH_TYPES.includes(pt) });
                  }}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm">
                  <option value="annual_dues">Annual Dues</option>
                  <option value="registration">Registration</option>
                  <option value="event">Event Fee</option>
                  <option value="agm">AGM</option>
                  <option value="mcpd">MCPD</option>
                  <option value="form">Form</option>
                  <option value="hotel">Hotel</option>
                  <option value="penalty">Penalty</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Amount (₦)</label>
                <input type="number" step="0.01" value={editData.amount}
                  onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                <input type="text" value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                  placeholder="Optional note" />
              </div>
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                <input type="checkbox" id="edit-passthrough" checked={editData.passthrough}
                  onChange={(e) => setEditData({ ...editData, passthrough: e.target.checked })}
                  className="w-4 h-4 accent-[#0B5D1E] rounded" />
                <label htmlFor="edit-passthrough" className="text-sm text-amber-800">
                  <strong>Pass-through</strong> — Debit &amp; Credit equal, <em>no balance effect</em>
                </label>
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-5">
              <button type="button" onClick={() => setEditingPayment(null)}
                className="px-5 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => handleApprove(editingPayment.id, editData)}
                disabled={approvingId === editingPayment.id}
                className="px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition disabled:opacity-50">
                {approvingId === editingPayment.id ? 'Approving...' : 'Save & Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Detail View Modal */}
      {viewingPayment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setViewingPayment(null)}>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0B5D1E] to-[#1A7A35] px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-base">Payment Details</h3>
                  <p className="text-white/70 text-xs mt-0.5">{viewingPayment.member_name}</p>
                </div>
                <button onClick={() => setViewingPayment(null)}
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Status & Amount */}
              <div className="text-center bg-gray-50 rounded-xl py-4">
                <span className={`inline-block text-xs px-3 py-1 rounded-full font-semibold mb-2 ${
                  viewingPayment.status === 'completed' ? 'bg-green-100 text-green-700' :
                  viewingPayment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  viewingPayment.status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                  'bg-red-100 text-red-700'
                }`}>{viewingPayment.status?.toUpperCase()}</span>
                <p className="text-2xl font-bold text-gray-900">₦{Number(viewingPayment.amount).toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1 capitalize">{viewingPayment.payment_type?.replace('_', ' ')}</p>
              </div>

              {/* Member Info */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                  Member
                </h4>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Name</span>
                    <span className="text-sm font-semibold text-gray-900">{viewingPayment.member_name || '—'}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Membership No.</span>
                    <span className="text-sm font-mono font-semibold text-gray-900">{viewingPayment.member_membership_number || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div>
                <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/></svg>
                  Payment Info
                </h4>
                <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Method</span>
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-semibold ${
                      viewingPayment.payment_method === 'paystack' ? 'bg-blue-100 text-blue-700' :
                      viewingPayment.payment_method === 'manual' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{viewingPayment.payment_method === 'paystack' ? 'Paystack' : viewingPayment.payment_method === 'manual' ? 'Bank Transfer' : viewingPayment.payment_method || '—'}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Reference</span>
                    <span className="text-sm font-mono font-semibold text-gray-900">{viewingPayment.reference}</span>
                  </div>
                  {viewingPayment.transaction_id && (
                    <>
                      <div className="border-t border-gray-200" />
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Transaction ID</span>
                        <span className="text-sm font-mono text-gray-700">{viewingPayment.transaction_id}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Year</span>
                    <span className="text-sm font-semibold text-gray-900">{viewingPayment.year}</span>
                  </div>
                  <div className="border-t border-gray-200" />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Date Initiated</span>
                    <span className="text-sm text-gray-900">{new Date(viewingPayment.created_at).toLocaleString()}</span>
                  </div>
                  {viewingPayment.paid_at && (
                    <>
                      <div className="border-t border-gray-200" />
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Date Paid</span>
                        <span className="text-sm text-gray-900">{new Date(viewingPayment.paid_at).toLocaleString()}</span>
                      </div>
                    </>
                  )}
                  {viewingPayment.receipt_number && (
                    <>
                      <div className="border-t border-gray-200" />
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Receipt No.</span>
                        <span className="text-sm font-semibold text-gray-900">{viewingPayment.receipt_number}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Description */}
              {viewingPayment.description && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-sm text-gray-800">{viewingPayment.description}</p>
                </div>
              )}

              {/* Proof of Payment */}
              {viewingPayment.proof_of_payment && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-2">Proof of Payment</p>
                  {/\.(jpe?g|png)$/i.test(viewingPayment.proof_of_payment.split('?')[0]) ? (
                    <a href={viewingPayment.proof_of_payment} target="_blank" rel="noopener noreferrer" title="Open full size">
                      <img src={viewingPayment.proof_of_payment} alt="Proof of payment"
                        className="w-full max-h-64 object-contain rounded-lg border bg-white" />
                    </a>
                  ) : (
                    <a href={viewingPayment.proof_of_payment} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#0B5D1E] hover:underline">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                      View uploaded proof (PDF)
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 pb-4 pt-2 flex-shrink-0">
              <button type="button" onClick={() => setViewingPayment(null)}
                className="w-full py-3 rounded-xl bg-[#0B5D1E] text-white font-bold text-sm hover:bg-[#062E10] transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search & Date Range Filter */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input type="text" value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search member, ID, trans no, description..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
          </div>
          {(searchQuery || dateFrom || dateTo) && (
            <button onClick={() => { setSearchQuery(''); setDateFrom(''); setDateTo(''); }}
              className="px-4 py-2 border rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition whitespace-nowrap">
              Clear Filters
            </button>
          )}
        </div>
        {(searchQuery || dateFrom || dateTo) && (
          <p className="text-xs text-gray-500 mt-2">Showing {filteredEntries.length} of {ledgerEntries.length} entries</p>
        )}
      </div>

      <div className="flex justify-end gap-2 mb-4">
        <button onClick={downloadPDF}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" /></svg>
          Download PDF
        </button>
        <button onClick={exportExcel}
          className="bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-800 transition flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          Export Excel
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">S/N</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member ID</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Member</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Trans No</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Debit Amt</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Credit Amt</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="9" className="text-center py-8 text-gray-500">Loading...</td></tr>
            ) : filteredEntries.length === 0 ? (
              <tr><td colSpan="9" className="text-center py-8 text-gray-400 text-sm">{ledgerEntries.length > 0 ? 'No matching transactions found.' : 'No transactions recorded yet.'}</td></tr>
            ) : paginatedEntries.map((e, index) => (
              <tr key={e.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-500">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td className="px-4 py-3 text-sm font-mono text-gray-600">{e.membership_number || '—'}</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{e.member_name}</td>
                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{e.date}</td>
                <td className="px-4 py-3 text-sm font-mono text-gray-700">{e.transaction_number}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                    e.entry_type === 'SJ' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                  }`}>{e.entry_type}</span>
                </td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">
                  {Number(e.debit_amount) > 0 ? `₦${Number(e.debit_amount).toLocaleString()}` : ''}
                </td>
                <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                  {Number(e.credit_amount) > 0 ? `₦${Number(e.credit_amount).toLocaleString()}` : ''}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{e.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {filteredEntries.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-xl shadow-sm border px-4 py-3 mt-4 gap-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>Show</span>
            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              className="border rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-[#0B5D1E] outline-none">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <span>per page</span>
            <span className="text-gray-400 ml-2">|</span>
            <span className="ml-2">
              {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, filteredEntries.length)} of {filteredEntries.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
              className="px-2.5 py-1.5 border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              «
            </button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1.5 border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              ‹ Prev
            </button>
            <span className="px-3 py-1.5 text-sm font-semibold text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1.5 border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              Next ›
            </button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
              className="px-2.5 py-1.5 border rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              »
            </button>
          </div>
        </div>
      )}

      {/* Bulk Debit/Credit Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowBulkModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${bulkType === 'SJ' ? 'bg-red-100' : 'bg-blue-100'}`}>
                <svg className={`w-5 h-5 ${bulkType === 'SJ' ? 'text-red-600' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {bulkType === 'SJ' ? 'Bulk Debit' : 'Bulk Credit'} All Members
                </h3>
                <p className="text-xs text-gray-500">
                  This will create a {bulkType === 'SJ' ? 'debit (charge)' : 'credit (payment)'} entry for every active member.
                </p>
              </div>
            </div>

            <form onSubmit={handleBulkSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Date</label>
                  <input type="date" required value={bulkData.date}
                    onChange={(e) => setBulkData({ ...bulkData, date: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Amount (₦)</label>
                  <input type="number" step="0.01" required value={bulkData.amount}
                    onChange={(e) => setBulkData({ ...bulkData, amount: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                    placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                  <input type="text" required value={bulkData.description}
                    onChange={(e) => setBulkData({ ...bulkData, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none text-sm"
                    placeholder={bulkType === 'SJ' ? 'e.g. Annual Dues 2026' : 'e.g. Payment received'} />
                </div>

                {/* Pass-through for CRJ */}
                {bulkType === 'CRJ' && (
                  <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                    <input type="checkbox" id="bulk-passthrough" checked={bulkData.passthrough}
                      onChange={(e) => setBulkData({ ...bulkData, passthrough: e.target.checked })}
                      className="w-4 h-4 accent-[#0B5D1E] rounded" />
                    <label htmlFor="bulk-passthrough" className="text-sm text-amber-800">
                      <strong>Pass-through</strong> — Debit &amp; Credit equal, <em>no balance effect</em>
                    </label>
                  </div>
                )}

                <div className={`rounded-lg border px-4 py-3 text-sm ${bulkType === 'SJ' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                  <strong>Warning:</strong> This action will affect <strong>all active members</strong> and cannot be easily undone.
                </div>
              </div>

              <div className="flex gap-2 justify-end mt-5">
                <button type="button" onClick={() => setShowBulkModal(false)}
                  className="px-5 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={bulkLoading}
                  className={`px-5 py-2 rounded-lg text-white text-sm font-semibold transition disabled:opacity-50 ${
                    bulkType === 'SJ' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                  }`}>
                  {bulkLoading ? 'Processing...' : `${bulkType === 'SJ' ? 'Debit' : 'Credit'} All Members`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reverse Bulk Modal */}
      {showReverseModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowReverseModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Reverse Bulk Operation</h3>
                <p className="text-xs text-gray-500">Select a bulk batch to reverse. This will delete all entries in that batch.</p>
              </div>
            </div>

            {batchesLoading ? (
              <div className="text-center py-8 text-gray-500">Loading batches...</div>
            ) : bulkBatches.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No bulk batches found. Future bulk operations will appear here.</div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Description</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Entries</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Total Debit</th>
                      <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Total Credit</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bulkBatches.map(b => (
                      <tr key={b.batch_id} className="hover:bg-amber-50/50">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{b.date}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                            b.entry_type === 'SJ' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                          }`}>{b.entry_type}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{b.description || '—'}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold">{b.count}</td>
                        <td className="px-4 py-3 text-sm text-right text-red-600 font-semibold">
                          {Number(b.total_debit) > 0 ? `₦${Number(b.total_debit).toLocaleString()}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-green-600 font-semibold">
                          {Number(b.total_credit) > 0 ? `₦${Number(b.total_credit).toLocaleString()}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleReverseBatch(b.batch_id)}
                            disabled={reversingBatchId === b.batch_id}
                            className="bg-red-500 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-red-600 transition disabled:opacity-50">
                            {reversingBatchId === b.batch_id ? 'Reversing...' : 'Reverse'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end mt-5">
              <button onClick={() => setShowReverseModal(false)}
                className="px-5 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
